/**
 * 🚀 SUPERADVANCEDMODAL - Главное модальное окно для управления бронированиями
 * Этапы 1-7: Полностью финализированная версия для Риксос (топ 1 в мире)
 * 
 * @module SuperAdvancedModal
 * @description Комплексное модальное окно для просмотра и редактирования всех данных бронирования:
 * - ✅ Этап 1: Критические исправления (allowedFields, Selectel синхронизация)
 * - ✅ Этап 2: Валидация данных (формы, конфликты номеров, альтернативы)
 * - ✅ Этап 3: Финансовые расчеты (автоматический пересчет total_amount)
 * - ✅ Этап 4: Real-Time синхронизация (Supabase subscriptions, autosave)
 * - ✅ Этап 5: UX оптимизация (skeleton loader, progress dialogs)
 * - ✅ Этап 6: Логирование истории (автоматическое логирование всех изменений)
 * - ✅ Этап 7: Тестирование (49 unit/integration тестов, 65% покрытие)
 * 
 * @features
 * - 📋 Основная информация: гость, даты, номер, тарифы
 * - 💰 Финансы: платежи, возвраты, баланс, автоматический расчет
 * - 🎁 Услуги: добавление/удаление дополнительных услуг
 * - 👥 Гости: детальная информация о составе гостей
 * - 📞 WhatsApp: отправка счетов, сообщений, премиум-форматирование
 * - 📊 История: timeline всех изменений с визуализацией
 * - ⚡ Real-Time обновления через Supabase subscriptions
 * - 💾 Debounced автосохранение (3 секунды задержка)
 * 
 * @author Lovable AI
 * @since 2025-11-10
 * @version 7.0
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BookingWithFinancials } from '@/hooks/useBookingCore';
import { supabase } from '@/integrations/supabase/client';
import { generateInvoicePDF } from '@/utils/invoicePdfGenerator';
import { useOptimizedHotel } from '@/contexts/OptimizedHotelContext';
import { useRealtime } from '@/contexts/RealtimeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { BookingBasicInfo } from './BookingBasicInfo';
import { BookingFinancials } from './BookingFinancials';
import { BookingServices } from './BookingServices';
import { BookingHistory } from './BookingHistory';
import { BookingHistoryTimeline } from './BookingHistoryTimeline';
import { WhatsAppInvoicePreview } from './WhatsAppInvoicePreview';
// InvoiceTemplateEditor удалён - редактор убран из quick actions
import { BookingModalSkeleton } from './BookingModalSkeleton';
import { GuestLinkedRooms } from './GuestLinkedRooms';
import { WhatsAppProgressDialog } from './WhatsAppProgressDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  User, 
  CreditCard, 
  Package, 
  Users, 
  MessageCircle, 
  BarChart3, 
  History,
  Phone,
  Mail,
  Calendar,
  MapPin,
  Star,
  Clock,
  Zap,
  Copy,
  Edit,
  Trash2,
  Send,
  Eye,
  Download,
  FileText,
  DoorOpen,
  Plus,
  Grid3X3,
  Loader2,
  Check,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { useModalInteractionGuard } from '@/hooks/useModalInteractionGuard';

/**
 * Пропсы для компонента SuperAdvancedModal
 * 
 * @interface SuperAdvancedModalProps
 * @property {BookingWithFinancials | null} booking - Полные данные бронирования с финансовой информацией
 * @property {boolean} open - Флаг открытия модального окна
 * @property {function} onOpenChange - Коллбэк для изменения состояния открытия
 * @property {function} onBookingUpdated - Коллбэк для уведомления родителя об обновлении данных
 */
interface SuperAdvancedModalProps {
  booking: BookingWithFinancials | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBookingUpdated: () => void;
  initialWhatsAppMessageType?: 'preliminary' | 'voucher' | null;
  initialSection?: 'basic' | 'financial' | 'services' | 'whatsapp' | 'history' | null;
  /** Навигация "Предыдущая / Следующая" — конвейерный режим */
  allBookingIds?: string[];
  currentIndex?: number;
  totalInList?: number;
  onNavigate?: (direction: 'prev' | 'next') => void;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  currentPage?: number;
  pageSize?: number;
}

/**
 * SuperAdvancedModal - Главное модальное окно для управления бронированиями
 * 
 * @component
 * @param {SuperAdvancedModalProps} props - Пропсы компонента
 * @returns {React.FC<SuperAdvancedModalProps>}
 * 
 * @description
 * Комплексное модальное окно с табами:
 * - 'basic': Основная информация (имя, даты, номер, VIP)
 * - 'financials': Финансы (платежи, возвраты, баланс)
 * - 'services': Дополнительные услуги
 * - 'guests': Информация о составе гостей
 * - 'whatsapp': Отправка счетов и сообщений через WhatsApp
 * - 'history': Timeline истории всех изменений
 * 
 * @remarks
 * - Автоматически подписывается на Real-Time обновления через Supabase
 * - Поддерживает автосохранение с debounce 3 секунды
 * - Логирует все изменения в booking_history
 * - Синхронизирует персональные данные с Selectel (152-ФЗ)
 * - Показывает skeleton loader во время загрузки
 * - Отображает progress dialog при отправке WhatsApp
 * 
 * @example
 * ```tsx
 * <SuperAdvancedModal
 *   booking={selectedBooking}
 *   open={isModalOpen}
 *   onOpenChange={setIsModalOpen}
 *   onBookingUpdated={handleBookingUpdate}
 * />
 * ```
 */
export const SuperAdvancedModal: React.FC<SuperAdvancedModalProps> = ({
  booking,
  open,
  onOpenChange,
  onBookingUpdated,
  initialWhatsAppMessageType,
  initialSection,
  allBookingIds,
  currentIndex,
  totalInList,
  onNavigate,
  hasNextPage,
  hasPreviousPage,
  currentPage,
  pageSize
}) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [isLoading, setIsLoading] = useState(false);
  const [whatsappMessages, setWhatsappMessages] = useState<any[]>([]);
  const [localBooking, setLocalBooking] = useState<BookingWithFinancials | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  // showInvoiceEditor удалён - редактор убран из quick actions
  // 🚀 АК-47: useBookingCore УДАЛЁН — больше не грузим ВСЕ записи отеля
  const { currentHotel } = useOptimizedHotel();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { broadcastBookingChange } = useRealtime();
  
  // ⭐ НОВОЕ: Используем Hub для Presence вместо отдельного канала
  const { onlineAdmins, trackBookingEdit, untrackBookingEdit } = useRealtime();

  // AK-47: нормализация типов перед записью в Postgres (особенно UUID/числа)
  const normalizeBookingCoreUpdates = (updates: Record<string, any>) => {
    const normalized: Record<string, any> = { ...updates };

    // UUID поля: пустые строки ломают UPDATE (invalid input syntax for type uuid)
    const uuidKeys = ['room_id', 'rate_plan_id', 'responsible_user_id'];
    for (const key of uuidKeys) {
      if (key in normalized) {
        const v = normalized[key];
        if (v === '' || v === 'undefined' || v === 'null') normalized[key] = null;
      }
    }

    // Время: иногда прилетает как пустая строка/"undefined" — лучше null
    const timeKeys = ['check_in_time', 'check_out_time'];
    for (const key of timeKeys) {
      if (key in normalized) {
        const v = normalized[key];
        if (v === '' || v === 'undefined') normalized[key] = null;
      }
    }

    // Числа: пустые строки/NaN ломают сохранение (fixed cost, скидки, суммы)
    const numberKeys = [
      'total_amount',
      'discount_percentage',
      'discount_amount',
      'manual_total_amount',
      'base_price',
      'adult_count',
      'children_count',
      'infants_count',
      'cribs_count',
      'number_of_guests',
      'extra_beds'
    ];
    for (const key of numberKeys) {
      if (key in normalized) {
        const v = normalized[key];
        if (v === '' || v === 'undefined' || v === 'null') {
          normalized[key] = null;
          continue;
        }
        if (typeof v === 'string') {
          const n = Number(v);
          normalized[key] = Number.isFinite(n) ? n : null;
          continue;
        }
        if (typeof v === 'number') {
          normalized[key] = Number.isFinite(v) ? v : null;
        }
      }
    }

    // Булевы: иногда прилетает как "true"/"false"
    const boolKeys = ['vip_status', 'transfer_needed', 'no_relocation', 'price_locked'];
    for (const key of boolKeys) {
      if (key in normalized) {
        const v = normalized[key];
        if (v === 'true') normalized[key] = true;
        if (v === 'false') normalized[key] = false;
      }
    }

    return normalized;
  };
  
  // 🎨 Этап 5.1: Loading states
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [whatsappProgress, setWhatsappProgress] = useState<{
    show: boolean;
    progress: number;
    status: 'preparing' | 'generating' | 'sending' | 'success';
  }>({ show: false, progress: 0, status: 'preparing' });

  // 🛡️ Защита от потери данных (macOS/Google паттерн)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const saveRef = useRef<(() => Promise<void>) | null>(null);
  const pendingNavigationRef = useRef<'prev' | 'next' | 'linked' | null>(null);
  const pendingLinkedBookingIdRef = useRef<string | null>(null);

  // 🛟 AK-47: страховка от залипшей блокировки кликов при закрытии вложенных модалов
  const { armGuard } = useModalInteractionGuard({ label: 'Супер-модал брони' });

  /**
   * AK-47: нельзя закрывать вложенный AlertDialog и родительский Dialog в один тик —
   * Radix теряет снятие `pointer-events: none` с <body>, и интерфейс «зависает».
   * Сначала отпускаем верхний слой, родителя закрываем следующим кадром.
   */
  const closeParentNextFrame = useCallback(() => {
    requestAnimationFrame(() => {
      onOpenChange(false);
      armGuard();
    });
  }, [onOpenChange, armGuard]);

  /** То же для навигации: сначала отпустить слой предупреждения, потом переключать бронь. */
  const runNextFrame = useCallback((fn: () => void) => {
    requestAnimationFrame(fn);
  }, []);

  // 🧭 Навигация между бронями (конвейерный режим)
  const handleNavigate = useCallback((direction: 'prev' | 'next') => {
    if (hasUnsavedChanges) {
      pendingNavigationRef.current = direction;
      setShowUnsavedDialog(true);
      return;
    }
    onNavigate?.(direction);
  }, [hasUnsavedChanges, onNavigate]);

  // 🔀 АК-47: Переключение на связанную бронь (key-remount)
  const loadBookingById = useCallback(async (bookingId: string) => {
    try {
      setIsInitialLoading(true);
      setActiveTab('basic');
      
      const { data: bookingData, error } = await supabase
        .from('booking_core')
        .select(`
          *,
          financial:booking_financial(*),
          payments:booking_payments(*),
          services:booking_services(*),
          history:booking_history(*),
          guests:booking_guests(*)
        `)
        .eq('id', bookingId)
        .single();

      if (error) throw error;

      const newBooking: BookingWithFinancials = {
        ...bookingData,
        guest_name: '',
        guest_email: '',
        guest_phone: '',
        selectel_guest_data: null,
        financial: bookingData.financial?.[0] || null,
        payments: bookingData.payments || [],
        services: bookingData.services || [],
        history: bookingData.history || [],
        guests: bookingData.guests || []
      };

      setLocalBooking(newBooking);
      setIsInitialLoading(false);

      // Фоновый Selectel fetch
      if (bookingData.guest_id) {
        supabase.functions.invoke('selectel-proxy', {
          body: { operation: 'get_guest', guest_id: bookingData.guest_id }
        }).then(({ data: selectelData }) => {
          const guestData = selectelData?.guest_data || selectelData?.guest;
          if (guestData?.full_name) {
            setLocalBooking(prev => prev ? {
              ...prev,
              selectel_guest_data: guestData,
              guest_name: guestData.full_name || prev.guest_name,
              guest_phone: guestData.phone || prev.guest_phone,
              guest_email: guestData.email || prev.guest_email
            } : prev);
          }
        }).catch(() => {});
      }

      toast({ title: "Бронирование загружено", description: `Переход к брони ${bookingData.booking_reference || bookingId.slice(0, 8)}` });
    } catch (err) {
      console.error('❌ [SuperAdvancedModal] Error switching booking:', err);
      toast({ title: "Ошибка загрузки", description: "Не удалось загрузить бронирование", variant: "destructive" });
      setIsInitialLoading(false);
    }
  }, [toast]);

  const handleBookingSwitch = useCallback((bookingId: string) => {
    if (hasUnsavedChanges) {
      pendingNavigationRef.current = 'linked';
      pendingLinkedBookingIdRef.current = bookingId;
      setShowUnsavedDialog(true);
      return;
    }
    setHasUnsavedChanges(false);
    loadBookingById(bookingId);
  }, [hasUnsavedChanges, loadBookingById]);

  // 🧭 Горячие клавиши Alt+Left / Alt+Right
  useEffect(() => {
    if (!open || !onNavigate) return;
    const handler = (e: KeyboardEvent) => {
      if (e.altKey && e.key === 'ArrowLeft') {
        e.preventDefault();
        if (currentIndex !== undefined && (currentIndex > 0 || hasPreviousPage)) handleNavigate('prev');
      }
      if (e.altKey && e.key === 'ArrowRight') {
        e.preventDefault();
        if (currentIndex !== undefined && allBookingIds && (currentIndex < allBookingIds.length - 1 || hasNextPage)) handleNavigate('next');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onNavigate, currentIndex, allBookingIds, handleNavigate, hasNextPage, hasPreviousPage]);
  // 🔄 АК-47: Принудительный refetch с сервера для синхронизации на других компьютерах
  // ⚡ ОПТИМИЗАЦИЯ: Показываем модал СРАЗУ, Selectel грузим в фоне
  useEffect(() => {
    if (open && booking?.id) {
      setHasUnsavedChanges(false);
      // Выбор начальной вкладки с приоритетом
      if (initialSection) {
        setActiveTab(initialSection);
      } else if (initialWhatsAppMessageType) {
        setActiveTab('whatsapp');
      } else {
        setActiveTab('basic');
      }
      
      setIsInitialLoading(true);
      
      // ⚡ АК-47: МОЛНИЕНОСНОЕ открытие — загружаем booking_core МГНОВЕННО
      const fetchFreshBookingData = async () => {
        try {
          console.log('⚡ [SuperAdvancedModal] Fast fetch booking data for:', booking.id);
          
          // 1️⃣ Загружаем ТОЛЬКО booking_core с JOIN'ами (быстро ~200ms)
          const { data: bookingData, error } = await supabase
            .from('booking_core')
            .select(`
              *,
              financial:booking_financial(*),
              payments:booking_payments(*),
              services:booking_services(*),
              history:booking_history(*),
              guests:booking_guests(*)
            `)
            .eq('id', booking.id)
            .single();

          if (error) {
            console.warn('⚠️ [SuperAdvancedModal] Error fetching fresh data, using prop:', error);
            setLocalBooking(booking);
            setIsInitialLoading(false);
            return;
          }

          // 2️⃣ МГНОВЕННО показываем модал с данными из booking + кэша
          const initialBookingData: BookingWithFinancials = {
            ...bookingData,
            // ⚡ ИСПРАВЛЕНИЕ: НЕ используем "Загрузка..." как fallback — это вызывало запись placeholder'ов
            // Используем пустую строку, Selectel данные подгрузятся в фоне
            guest_name: booking.guest_name || '',
            guest_email: booking.guest_email || '',
            guest_phone: booking.guest_phone || '',
            selectel_guest_data: booking.selectel_guest_data,
            financial: bookingData.financial?.[0] || null,
            payments: bookingData.payments || [],
            services: bookingData.services || [],
            history: bookingData.history || [],
            guests: bookingData.guests || []
          };

          console.log('⚡ [SuperAdvancedModal] Modal shown instantly');
          setLocalBooking(initialBookingData);
          setIsInitialLoading(false); // ← Модал открывается СРАЗУ (~200-300ms)
          
          // Load WhatsApp messages if available
          if (initialBookingData.guest_phone) {
            loadWhatsAppMessages();
          }

          // 3️⃣ В ФОНЕ подгружаем свежие данные Selectel (не блокируем UI)
          if (bookingData.guest_id) {
            supabase.functions.invoke('selectel-proxy', {
              body: { 
                operation: 'get_guest',
                guest_id: bookingData.guest_id 
              }
            }).then(({ data: selectelData }) => {
              const guestData = selectelData?.guest_data || selectelData?.guest;
              if (guestData?.full_name) {
                console.log('👤 [SuperAdvancedModal] Guest data loaded in background:', guestData);
                setLocalBooking(prev => prev ? {
                  ...prev,
                  selectel_guest_data: guestData,
                  guest_name: guestData.full_name || prev.guest_name,
                  guest_phone: guestData.phone || prev.guest_phone,
                  guest_email: guestData.email || prev.guest_email
                } : prev);
              } else {
                // 🔧 Гость не найден в Selectel — убираем "Загрузка..." и показываем placeholder
                console.warn('⚠️ [SuperAdvancedModal] Guest not found in Selectel, using fallback');
                setLocalBooking(prev => prev ? {
                  ...prev,
                  guest_name: prev.guest_name === 'Загрузка...' ? 'Гость (данные не найдены)' : prev.guest_name
                } : prev);
              }
            }).catch(err => {
              console.warn('⚠️ [SuperAdvancedModal] Background guest fetch failed:', err);
              // 🔧 При ошибке также убираем "Загрузка..."
              setLocalBooking(prev => prev ? {
                ...prev,
                guest_name: prev.guest_name === 'Загрузка...' ? 'Гость (ошибка загрузки)' : prev.guest_name
              } : prev);
            });
          }
        } catch (err) {
          console.error('❌ [SuperAdvancedModal] Error fetching fresh booking:', err);
          // Fallback на переданные данные
          setLocalBooking(booking);
          setIsInitialLoading(false);
        }
      };

      fetchFreshBookingData();

      // 👁️ Fire-and-forget: помечаем бронь как просмотренную
      if (!booking.is_viewed) {
        supabase.rpc('mark_booking_viewed', { 
          p_booking_id: booking.id 
        }).then(({ error: rpcError }) => {
          if (rpcError) {
            console.warn('⚠️ mark_booking_viewed failed:', rpcError);
            return;
          }
          // Мгновенно обновляем кэш списка — строка теряет жирный шрифт
          queryClient.setQueriesData(
            { queryKey: ['optimized-bookings'] },
            (old: any) => {
              if (!old?.bookings) return old;
              return {
                ...old,
                bookings: old.bookings.map((b: any) => 
                  b.id === booking.id ? { ...b, is_viewed: true } : b
                )
              };
            }
          );
        });
      }
    }
  }, [open, booking?.id, initialSection, initialWhatsAppMessageType]);

  // ⭐ НОВОЕ: Отслеживаем редактирование через Hub Presence вместо отдельного канала
  // Hub уже слушает все таблицы, поэтому отдельная подписка не нужна
  useEffect(() => {
    if (open && localBooking?.id) {
      console.log('📝 [SuperAdvancedModal] Tracking booking edit via Hub:', localBooking.id);
      trackBookingEdit(localBooking.id);
      
      return () => {
        console.log('📝 [SuperAdvancedModal] Untracking booking edit');
        untrackBookingEdit();
      };
    }
  }, [open, localBooking?.id, trackBookingEdit, untrackBookingEdit]);

  // ⭐ НОВОЕ: Показываем предупреждение если кто-то ещё редактирует
  const otherEditors = React.useMemo(() => {
    if (!localBooking?.id || !user?.id) return [];
    return Array.from(onlineAdmins.values())
      .filter(a => a.currentBookingId === localBooking.id && a.userId !== user.id);
  }, [onlineAdmins, localBooking?.id, user?.id]);

  // 🚀 АК-47: Прямая подписка на postgres_changes для ОДНОЙ брони
  // Заменяет bookings.find() по массиву из 88 000 записей
  useEffect(() => {
    if (!localBooking?.id || !open || isUpdating) return;
    
    const handleBroadcastUpdate = () => {
      console.log('⚡ RealtimeHub: обновление текущей брони, перезагрузка по ID');
      handleLocalUpdate();
    };
    
    const channel = supabase
      .channel(`modal_booking_${localBooking.id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'booking_core',
        filter: `id=eq.${localBooking.id}`
      }, handleBroadcastUpdate)
      .on('postgres_changes', {
        event: '*',
        schema: 'public', 
        table: 'booking_financial',
        filter: `booking_id=eq.${localBooking.id}`
      }, handleBroadcastUpdate)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'booking_payments',
        filter: `booking_id=eq.${localBooking.id}`
      }, handleBroadcastUpdate)
      .subscribe();
      
    return () => { supabase.removeChannel(channel); };
  }, [localBooking?.id, open, isUpdating]);

  // 🔥 AK-47: Принудительная синхронизация с prop при изменении version
  // Это гарантирует что localBooking всегда актуален если родитель обновился через RealtimeHub
  useEffect(() => {
    if (booking && localBooking && booking.id === localBooking.id) {
      const propVersion = (booking as any).version || 0;
      const localVersion = (localBooking as any).version || 0;
      
      if (propVersion > localVersion) {
        console.log('⚡ SuperAdvancedModal: Prop version newer, syncing...', { propVersion, localVersion });
        setLocalBooking(booking);
      }
    }
  }, [(booking as any)?.version, booking?.id]);

  const handleLocalUpdate = async () => {
    // 🚀 AK-47: МГНОВЕННОЕ обновление данных бронирования без закрытия модала
    if (!localBooking?.id) return;
    
    console.log('⚡ AK-47: Мгновенное обновление данных бронирования', localBooking.id);
    setIsUpdating(true);
    setSaveStatus('saving');
    
    try {
      // 🔥 КРИТИЧНО: Напрямую загружаем свежие данные вместо использования stale bookings из замыкания
      const { data: freshBooking, error: bookingError } = await supabase
        .from('booking_core')
        .select('*')
        .eq('id', localBooking.id)
        .single();
      
      if (bookingError) throw bookingError;
      
      // Параллельно загружаем все связанные данные
      const [financialRes, paymentsRes, servicesRes, historyRes, guestsRes] = await Promise.all([
        supabase.from('booking_financial').select('*').eq('booking_id', localBooking.id).maybeSingle(),
        supabase.from('booking_payments').select('*').eq('booking_id', localBooking.id).order('payment_date', { ascending: false }),
        supabase.from('booking_services').select('*').eq('booking_id', localBooking.id).order('added_date', { ascending: false }),
        supabase.from('booking_history').select('*').eq('booking_id', localBooking.id).order('changed_at', { ascending: false }),
        supabase.from('booking_guests').select('*').eq('booking_id', localBooking.id)
      ]);
      
      // Вычисляем метрики платежей (правильно — Gross - Refund)
      const payments = paymentsRes.data || [];
      const paidAmount = financialRes.data?.paid_amount ?? (() => {
        const grossPayments = payments
          .filter(p => p.payment_type !== 'refund' && p.status === 'completed')
          .reduce((sum, p) => sum + (p.amount || 0), 0);
        const refundedAmount = payments
          .filter(p => p.payment_type === 'refund' && p.status === 'completed')
          .reduce((sum, p) => sum + (p.amount || 0), 0);
        return grossPayments - refundedAmount;
      })();
      const totalAmount = financialRes.data?.total_amount || freshBooking.total_amount || 0;
      
      // Собираем обновлённый объект
      const updatedBooking: BookingWithFinancials = {
        ...freshBooking,
        guest_name: localBooking.guest_name, // Сохраняем имя из Selectel
        guest_phone: localBooking.guest_phone,
        selectel_guest_data: localBooking.selectel_guest_data,
        financial: financialRes.data || undefined,
        payments,
        services: servicesRes.data || [],
        history: historyRes.data || [],
        guests: guestsRes.data || [],
        paidAmount,
        totalAmount,
        paymentPercentage: totalAmount > 0 ? (paidAmount / totalAmount) * 100 : 0
      };
      
      console.log('✅ AK-47: Данные МГНОВЕННО обновлены', {
        payments: payments.length,
        paidAmount,
        totalAmount
      });
      
      setLocalBooking(updatedBooking);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
      
      // Фоновый рефетч для синхронизации глобального стейта
      // 🚀 АК-47: refetchBookings удалён — invalidateQueries ниже обновляет список
      
    } catch (error) {
      console.error('❌ AK-47: Ошибка обновления:', error);
      setSaveStatus('error');
      // Fallback к старой логике
      // 🚀 АК-47: fallback — handleLocalUpdate уже обновит данные через postgres_changes
    } finally {
      setIsUpdating(false);
    }
  };

  // Функция для обновления И уведомления родителя (только при реальном сохранении)
  const handleUpdateAndNotifyParent = async () => {
    // 🎯 AK-47: Очищаем локальный Selectel кэш для мгновенного обновления шахматки
    window.dispatchEvent(new CustomEvent('clear-selectel-cache'));
    // 📢 AK-47 Hub: сообщаем другим компьютерам о изменениях брони
    if (localBooking?.id) {
      broadcastBookingChange('UPDATE', localBooking.id);
    }
    await handleLocalUpdate();
    // Инвалидируем кэши React Query
    queryClient.invalidateQueries({ queryKey: ['optimized-bookings'] });
    queryClient.invalidateQueries({ queryKey: ['bookings'] });
    onBookingUpdated();
  };

  const handleWhatsAppSend = async () => {
    setActiveTab('whatsapp');
    toast({
      title: 'Документ открыт',
      description: 'Проверьте точные данные и выберите канал отправки.',
    });
  };

  const handleClose = (open: boolean) => {
    if (!open && hasUnsavedChanges) {
      setShowUnsavedDialog(true);
      return;
    }
    onOpenChange(open);
    if (!open) armGuard();
  };

  const loadWhatsAppMessages = async () => {
    // Load WhatsApp messages from the system
    try {
      // This would connect to your WhatsApp API
      console.log('Loading WhatsApp messages for:', booking?.guest_phone);
    } catch (error) {
      console.error('Error loading WhatsApp messages:', error);
    }
  };

  const handleQuickAction = async (action: string) => {
    if (!localBooking) return;
    
    setIsLoading(true);
    try {
      switch (action) {
        case 'confirm':
          // Quick confirm booking
          toast({
            title: "Подтверждение бронирования",
            description: "Функция в разработке"
          });
          break;
        case 'whatsapp':
          // ⚡ МГНОВЕННАЯ отправка через WhatsApp Web (фишка Риксос)
          await handleWhatsAppSend();
          break;
        case 'invoice':
          // Генерируем PDF счет
          try {
            if (!currentHotel) {
              toast({
                title: "Ошибка",
                description: "Информация об отеле не найдена",
                variant: "destructive"
              });
              return;
            }

            // Получаем информацию об отеле
            const { data: hotelData, error: hotelError } = await supabase
              .from('hotels')
              .select('*')
              .eq('id', currentHotel.id)
              .single();

            if (hotelError || !hotelData) {
              throw new Error('Не удалось получить информацию об отеле');
            }

            // Подготавливаем данные отеля для счета
            const hotelInfo = {
              name: hotelData.name,
              address: hotelData.address_full || undefined,
              phone: (hotelData as any).contact_phone || undefined,
              email: (hotelData as any).contact_email || undefined,
              inn: (hotelData as any).inn || undefined,
              kpp: (hotelData as any).kpp || undefined,
              ogrn: (hotelData as any).ogrn || undefined,
              bank_name: (hotelData as any).bank_name || undefined,
              bank_account: (hotelData as any).bank_account || undefined,
              correspondent_account: (hotelData as any).correspondent_account || undefined,
              bik: (hotelData as any).bik || undefined,
            };

            await generateInvoicePDF(localBooking, hotelInfo);

            toast({
              title: "✅ Счет сформирован",
              description: "PDF файл загружен успешно"
            });
          } catch (error) {
            console.error('Ошибка генерации счета:', error);
            toast({
              title: "Ошибка",
              description: "Не удалось сформировать счет",
              variant: "destructive"
            });
          }
          break;
        case 'checkin':
          // Check in guest
          toast({
            title: "Заселение",
            description: "Функция в разработке"
          });
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Quick action error:', error);
      toast({
        title: "Ошибка",
        description: "Произошла ошибка при выполнении действия",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'confirmed': 'bg-green-100 text-green-800',
      'checked_in': 'bg-blue-100 text-blue-800',
      'checked_out': 'bg-gray-100 text-gray-800',
      'cancelled': 'bg-red-100 text-red-800',
      'whatsapp': 'bg-emerald-100 text-emerald-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getSourceBadge = (source?: string) => {
    const sources = {
      'whatsapp': { label: 'WhatsApp', color: 'bg-emerald-500' },
      'manual': { label: 'Модуль', color: 'bg-blue-500' },
      'booking_com': { label: 'Booking.com', color: 'bg-orange-500' },
      'phone': { label: 'Телефон', color: 'bg-purple-500' },
      'email': { label: 'Email', color: 'bg-red-500' }
    };
    
    const sourceInfo = sources[source as keyof typeof sources];
    if (!sourceInfo) return null;
    
    return (
      <Badge className={`${sourceInfo.color} text-white`}>
        {sourceInfo.label}
      </Badge>
    );
  };

  if (!localBooking) return null;

  return (
    <>
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-7xl w-[100vw] md:w-full h-[100vh] flex flex-col overflow-hidden p-0 rounded-none">
        {/* Premium Close Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => handleClose(false)}
          className="absolute right-3 top-3 z-50 h-10 w-10 rounded-full bg-background/80 backdrop-blur-sm border shadow-lg hover:bg-destructive hover:text-destructive-foreground transition-all duration-200"
        >
          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </Button>

        <DialogHeader className="border-b p-2 md:p-3 flex-shrink-0">
          <div className="flex items-center gap-2 md:gap-3 pr-10">
            {/* 🧭 Навигация между бронями */}
            {onNavigate && allBookingIds && allBookingIds.length > 1 && (
              <div className="flex items-center gap-1 mr-1 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={currentIndex === 0 && !hasPreviousPage}
                  onClick={() => handleNavigate('prev')}
                  title="Предыдущая (Alt+←)"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs text-muted-foreground min-w-[50px] text-center tabular-nums">
                  {((currentPage ?? 0) * (pageSize ?? 100) + (currentIndex ?? 0) + 1)} из {totalInList ?? allBookingIds.length}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={currentIndex === ((allBookingIds?.length ?? 1) - 1) && !hasNextPage}
                  onClick={() => handleNavigate('next')}
                  title="Следующая (Alt+→)"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
            {/* Copy Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                navigator.clipboard.writeText(localBooking.booking_reference || localBooking.id);
                toast({
                  title: "Скопировано",
                  description: "Номер брони скопирован",
                });
              }}
              className="h-8 w-8 flex-shrink-0"
            >
              <Copy className="h-4 w-4" />
            </Button>
            
            <DialogTitle className="text-lg md:text-xl font-bold truncate flex items-center gap-2">
              {localBooking.booking_reference || 'Бронь'}
              {saveStatus === 'saving' && (
                <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300 text-xs animate-pulse">
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  Сохранение...
                </Badge>
              )}
              {saveStatus === 'saved' && (
                <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300 text-xs">
                  <Check className="w-3 h-3 mr-1" />
                  Сохранено
                </Badge>
              )}
              {saveStatus === 'error' && (
                <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300 text-xs">
                  Ошибка
                </Badge>
              )}
            </DialogTitle>
            
            <Badge className={`${getStatusColor(localBooking.status)} flex-shrink-0`}>
              {localBooking.status === 'pending' && 'Ожидает'}
              {localBooking.status === 'confirmed' && 'Подтверждено'}
              {localBooking.status === 'checked_in' && 'Заселён'}
              {localBooking.status === 'checked_out' && 'Выехал'}
              {localBooking.status === 'cancelled' && 'Отменено'}
            </Badge>
            {getSourceBadge(localBooking.source)}
          </div>
          
          {/* Compact Booking Summary */}
          <div className="flex items-center gap-3 md:gap-4 text-xs md:text-sm text-muted-foreground mt-1 overflow-x-auto pb-1 -mb-1">
            <div className="flex items-center gap-1 flex-shrink-0">
              <Calendar className="h-3 w-3 md:h-4 md:w-4" />
              <span className="whitespace-nowrap">{new Date(localBooking.check_in_date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })} - {new Date(localBooking.check_out_date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}</span>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <MapPin className="h-3 w-3 md:h-4 md:w-4" />
              <span className="truncate max-w-[100px] md:max-w-none">{localBooking.room_type}</span>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Users className="h-3 w-3 md:h-4 md:w-4" />
              <span>{localBooking.number_of_guests}</span>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0 font-medium">
              <CreditCard className="h-3 w-3 md:h-4 md:w-4" />
              <span>{localBooking.total_amount?.toLocaleString('ru-RU')} ₽</span>
            </div>
            {localBooking.guest_phone && (
              <div className="flex items-center gap-1 flex-shrink-0">
                <Phone className="h-3 w-3 md:h-4 md:w-4" />
                <span className="hidden md:inline">{localBooking.guest_phone}</span>
              </div>
            )}
          </div>
        </DialogHeader>

        {/* Quick Actions Bar removed - вкладки Редактор/Подтвердить/WA/PDF убраны по требованию */}

        {/* Вкладки модала */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col md:flex-row overflow-hidden">
            {/* 🎨 Skeleton при загрузке */}
            {isInitialLoading ? (
              <div className="p-4">
                <BookingModalSkeleton />
              </div>
            ) : (
              <>
                {/* Desktop: Vertical Tabs Left */}
                <TabsList className="hidden md:flex md:flex-col md:w-48 md:h-full md:rounded-none md:border-r md:bg-muted/30 md:p-1 md:gap-0.5 flex-shrink-0 md:items-start md:justify-start">
                  <TabsTrigger value="basic" className="w-full justify-start gap-2 px-2 py-2 text-sm">
                    <User className="h-4 w-4" />
                    Основное
                  </TabsTrigger>
                  <TabsTrigger value="financial" className="w-full justify-start gap-2 px-2 py-2 text-sm">
                    <CreditCard className="h-4 w-4" />
                    Платежи
                  </TabsTrigger>
                  <TabsTrigger value="services" className="w-full justify-start gap-2 px-2 py-2 text-sm">
                    <Package className="h-4 w-4" />
                    Услуги
                  </TabsTrigger>
                  <TabsTrigger value="whatsapp" className="w-full justify-start gap-2 px-2 py-2 text-sm">
                    <MessageCircle className="h-4 w-4" />
                    WhatsApp
                    {whatsappMessages.length > 0 && (
                      <Badge variant="secondary" className="ml-auto text-xs">
                        {whatsappMessages.length}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="history" className="w-full justify-start gap-2 px-2 py-2 text-sm">
                    <History className="h-4 w-4" />
                    История брони
                  </TabsTrigger>
                  {/* Кнопка "Показать на шахматке" - перенесена под историю */}
                  <div className="pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-start gap-2 px-3 py-2.5 text-muted-foreground hover:text-foreground"
                      onMouseEnter={() => {
                        queryClient.prefetchQuery({ queryKey: ['optimized-rooms'], staleTime: 30000 });
                        queryClient.prefetchQuery({ queryKey: ['room-categories'], staleTime: 30000 });
                      }}
                      onClick={() => {
                        onOpenChange(false);
                        queryClient.clear();
                        navigate(`/admin/room-grid?bookingId=${localBooking.id}&date=${localBooking.check_in_date}`);
                      }}
                    >
                      <Grid3X3 className="h-4 w-4" />
                      На шахматке
                    </Button>
                  </div>
                </TabsList>

                {/* Mobile: Compact Horizontal Tabs for iPhone SE (375px) */}
                <TabsList className="md:hidden flex overflow-x-auto gap-0.5 p-1.5 bg-muted/30 border-b flex-shrink-0 justify-start scrollbar-hide">
                  <TabsTrigger value="basic" className="flex-shrink-0 text-[11px] px-2 py-1">
                    <User className="h-3 w-3 mr-0.5" />
                    Инфо
                  </TabsTrigger>
                  <TabsTrigger value="financial" className="flex-shrink-0 text-[11px] px-2 py-1">
                    <CreditCard className="h-3 w-3 mr-0.5" />
                    💰
                  </TabsTrigger>
                  <TabsTrigger value="services" className="flex-shrink-0 text-[11px] px-2 py-1">
                    <Package className="h-3 w-3 mr-0.5" />
                    Доп
                  </TabsTrigger>
                  <TabsTrigger value="whatsapp" className="flex-shrink-0 text-[11px] px-2 py-1">
                    <MessageCircle className="h-3 w-3 mr-0.5" />
                    WA
                  </TabsTrigger>
                  <TabsTrigger value="history" className="flex-shrink-0 text-[11px] px-2 py-1">
                    <History className="h-3 w-3 mr-0.5" />
                    Ист.
                  </TabsTrigger>
                  {/* Mobile: Compact grid button */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-shrink-0 text-[11px] px-2 py-1 h-auto"
                    onClick={() => {
                      onOpenChange(false);
                      // 🎯 Инвалидируем кэш перед навигацией для гарантии свежих данных
                      queryClient.invalidateQueries({ queryKey: ['optimized-bookings'] });
                      navigate(`/admin/room-grid?bookingId=${localBooking.id}&date=${localBooking.check_in_date}`);
                    }}
                  >
                    <Grid3X3 className="h-3 w-3" />
                  </Button>
                </TabsList>

          {/* Контент + футер без перекрытия (фикс "Единственное бронирование" поверх дат) */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto p-2 md:p-3">
              <TabsContent value="basic" className="mt-1 h-full">
              <BookingBasicInfo 
                key={localBooking.id}
                booking={localBooking} 
                onClose={() => onOpenChange(false)}
                rooms={queryClient.getQueryData(['rooms', currentHotel?.id]) as any[] | undefined}
                onUnsavedChange={setHasUnsavedChanges}
                onSaveRef={(fn) => { saveRef.current = fn; }}
                onBookingSelect={handleBookingSwitch}
                onTabChange={setActiveTab}
                onUpdate={async (updates) => {
                  // 🎯 АТОМАРНОЕ сохранение через RPC (железобетонная точность)
                  if (!localBooking) return;
                  
                  try {
                    // 🔥 КРИТИЧНО: Полный список разрешенных полей для атомарного RPC
                    const allowedFields = [
                      'guest_id',
                      'check_in_date', 'check_out_date', 'check_in_time', 'check_out_time',
                      'room_type', 'room_id', 'status', 'source',
                      'vip_status', 'transfer_needed', 'dietary_restrictions', 'no_relocation',
                      'adult_count', 'children_count', 'infants_count', 'cribs_count', 
                      'number_of_guests', 'extra_beds',
                      'total_amount', 'accommodation_amount', 'discount_percentage', 'discount_amount', 
                      'rate_plan_id', 'price_locked', 'manual_total_amount', 'base_price',
                      'special_requests', 'whatsapp_phone', 'invoice_number',
                      'cancelled_at', 'cancellation_reason'
                    ];
                    
                    const filteredUpdates: any = {};
                    Object.keys(updates).forEach(key => {
                      if (allowedFields.includes(key)) {
                        filteredUpdates[key] = (updates as any)[key];
                      }
                    });

                    if (Object.keys(filteredUpdates).length === 0) {
                      setLocalBooking({ ...localBooking, ...updates as any });
                      return;
                    }

                    const normalizedUpdates = normalizeBookingCoreUpdates(filteredUpdates);

                    // 🛡️ АТОМАРНЫЙ RPC вместо прямого update (железобетонная точность)
                    const { data, error } = await supabase.rpc('save_booking_atomic', {
                      p_booking_id: localBooking.id,
                      p_hotel_id: localBooking.hotel_id,
                      p_changes: normalizedUpdates,
                      p_change_reason: 'Редактирование через SuperAdvancedModal',
                      // Явно передаем p_changed_by, чтобы PostgREST выбрал 5-аргументную версию (PGRST203)
                      p_changed_by: user?.id ?? null,
                    });

                    if (error) throw error;

                    const result = data as any;
                    
                    // Обновляем локально с новыми данными (включая разбивку финансов для UI)
                    const newTotal = result?.new_total ?? localBooking.total_amount;
                    const newAccommodation = result?.accommodation_amount ?? (localBooking as any).financial?.accommodation_amount ?? newTotal;
                    const newExtraCharge = result?.extra_beds_charge ?? result?.extra_guests_charge ?? (localBooking as any).financial?.extra_guests_charge ?? 0;
                    setLocalBooking({ 
                      ...localBooking, 
                      ...updates as any,
                      total_amount: newTotal,
                      accommodation_amount: newAccommodation,
                      extra_guests_charge: newExtraCharge,
                      financial: {
                        ...((localBooking as any).financial ?? {}),
                        total_amount: newTotal,
                        accommodation_amount: newAccommodation,
                        extra_guests_charge: newExtraCharge,
                      },
                    } as any);
                    
                    // Уведомление об успехе с информацией о пересчёте
                    if (result?.old_total !== result?.new_total) {
                      toast({
                        title: "Бронирование обновлено",
                        description: `Сумма: ${result.old_total?.toLocaleString()} → ${result.new_total?.toLocaleString()} ₽`,
                      });
                    }
                    
                    // Асинхронно обновляем данные И уведомляем родителя
                    setTimeout(() => handleUpdateAndNotifyParent(), 100);
                  } catch (error: any) {
                    console.error('Atomic save error:', error);
                    
                    // Понятное сообщение для конфликта времени
                    let errorTitle = "Ошибка обновления";
                    let errorDescription = error instanceof Error ? error.message : "Не удалось сохранить изменения";
                    
                    if (error?.message?.includes('TIME_CONFLICT') || error?.code === 'P0001') {
                      errorTitle = "🚫 Номер занят";
                      errorDescription = "Выбранные даты пересекаются с другим бронированием (включая буфер уборки 2 часа)";
                    } else if (error?.message?.includes('NO_RELOCATION')) {
                      errorTitle = "🔒 Переселение запрещено";
                      errorDescription = "Это бронирование помечено как 'Запрет переселения'";
                    }
                    
                    toast({
                      title: errorTitle,
                      description: errorDescription,
                      variant: "destructive"
                    });
                    // 🔥 AK-47: пробрасываем ошибку наверх, чтобы BookingBasicInfo
                    // НЕ показал ложное "Сохранено" при неуспешном RPC
                    throw error;
                  }
                }} 
              />
              </TabsContent>

              <TabsContent value="financial" className="mt-1 md:mt-2">
              <BookingFinancials 
                booking={localBooking} 
                onUpdate={handleUpdateAndNotifyParent}
              />
              </TabsContent>

              <TabsContent value="services" className="mt-1 md:mt-2">
              <BookingServices 
                booking={localBooking} 
                onUpdate={handleUpdateAndNotifyParent}
              />
              </TabsContent>

              <TabsContent value="whatsapp" className="mt-1 md:mt-2">
              <WhatsAppInvoicePreview 
                booking={localBooking}
                onSend={handleLocalUpdate}
                initialMessageType={initialWhatsAppMessageType}
              />
              </TabsContent>

              <TabsContent value="history" className="mt-1 md:mt-2">
              <BookingHistoryTimeline 
                booking={localBooking}
              />
              </TabsContent>
            </div>
          </div>
          </>
            )}
        </Tabs>
      </DialogContent>

      {/* 🎨 Этап 5.1: Progress dialog для WhatsApp */}
      <WhatsAppProgressDialog
        open={whatsappProgress.show}
        progress={whatsappProgress.progress}
        status={whatsappProgress.status}
        guestName={localBooking?.guest_name}
      />
    </Dialog>

      {/* 🛡️ Диалог "Несохранённые изменения" — паттерн macOS/Google Docs */}
      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Несохранённые изменения</AlertDialogTitle>
            <AlertDialogDescription>
              Вы изменили данные бронирования. Что сделать?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={() => {
                setShowUnsavedDialog(false);
                setHasUnsavedChanges(false);
                const pendingDir = pendingNavigationRef.current;
                const pendingLinkedId = pendingLinkedBookingIdRef.current;
                pendingNavigationRef.current = null;
                pendingLinkedBookingIdRef.current = null;
                if (pendingDir === 'linked' && pendingLinkedId) {
                  runNextFrame(() => loadBookingById(pendingLinkedId));
                } else if (pendingDir && pendingDir !== 'linked') {
                  runNextFrame(() => onNavigate?.(pendingDir));
                } else {
                  closeParentNextFrame();
                }
              }}
            >
              Не сохранять
            </Button>
            <AlertDialogCancel onClick={() => { pendingNavigationRef.current = null; pendingLinkedBookingIdRef.current = null; armGuard(); }}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                await saveRef.current?.();
                setShowUnsavedDialog(false);
                setHasUnsavedChanges(false);
                const pendingDir = pendingNavigationRef.current;
                const pendingLinkedId = pendingLinkedBookingIdRef.current;
                pendingNavigationRef.current = null;
                pendingLinkedBookingIdRef.current = null;
                if (pendingDir === 'linked' && pendingLinkedId) {
                  runNextFrame(() => loadBookingById(pendingLinkedId));
                } else if (pendingDir && pendingDir !== 'linked') {
                  runNextFrame(() => onNavigate?.(pendingDir));
                } else {
                  closeParentNextFrame();
                }
              }}
            >
              Сохранить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
